/**
 * Who can do what in team chat (Ben, 13 Oct 2026). Which spaces someone
 * sees is data/spaces.js; this is what they can do inside Team.
 *
 *   Managers (owners, and roles with Team) run the team chat:
 *     make channels, edit or tidy any channel, post in managers-only
 *     channels like #announcements, remove anyone's message
 *   Whoever made a channel can also edit it and remove people from it
 *   Groups belong to the people in them: anyone in one can rename it and
 *     add people; its maker (or a manager) can remove people
 *   Everyone channels (the salon's main channel): nobody leaves and nobody
 *     is removed, because everyone at the location is always in it
 *   Client chat settings (the away message): owners, self-employed
 *     stylists, and roles with Settings
 *
 * The UI only hides. The server checks each of these (backend brief, section 22).
 */
import { getAccess, isSelfEmployedMe, useAccess } from '../../permissions/access';
import { PENDING_CAPS } from '../../permissions/rolesStore';

/** Pure, for tests: access = { owner, caps }. */
export function powersFor(access, selfEmployed) {
    const a = access || {};
    const caps = a.caps || [];
    const manager = !!a.owner || caps.indexOf('team') !== -1;
    return {
        manager,
        createChannels: manager,
        clientSettings: !!a.owner || !!selfEmployed || caps.indexOf('settings') !== -1,
    };
}

export function myPowers() { return powersFor(getAccess(), isSelfEmployedMe()); }

/** Live: updates when the person's access loads or changes. */
export function usePowers() {
    const { access } = useAccess();
    return powersFor(access, isSelfEmployedMe());
}

const teamShared = (c) => !!c && c.space === 'team' && (c.kind === 'channel' || c.kind === 'group');

/** Rename it, change the topic and who can post. */
export function canEditChat(c, meId, powers) {
    if (!teamShared(c)) return false;
    if (c.kind === 'group') return true;
    return !!powers.manager || (!!c.createdBy && c.createdBy === meId);
}

export function canAddPeople(c, meId, powers) {
    if (!teamShared(c) || c.everyone) return false;
    return c.kind === 'group' || canEditChat(c, meId, powers);
}

export function canRemovePeople(c, meId, powers) {
    if (!teamShared(c) || c.everyone) return false;
    return !!powers.manager || (!!c.createdBy && c.createdBy === meId);
}

export function canLeave(c) {
    return teamShared(c) && !c.everyone;
}

/** Managers-only channels (#announcements): the rest of the team reads and reacts. */
export function canPost(c, powers) {
    if (!c || c.postBy !== 'managers') return true;
    return !!powers.manager;
}

/** A manager removing someone else's message in team chat. */
export function canModerate(message, c, meId, powers) {
    if (!powers.manager || !c || c.space !== 'team' || !message) return false;
    return message.authorId !== meId && !message.system && !message.auto && !message.deletedAt && message.authorId !== 'bailey';
}

/**
 * Texting and emailing clients costs the salon money (texting plan, 14 Oct
 * 2026). Owners and self-employed stylists always can; staff when their role
 * has the "Text and email clients" switch (client_texts). Until the server
 * knows that switch (rolesStore PENDING_CAPS) it follows All clients. The
 * server has the last word (reach.canSend, backend brief section 28).
 */
export function canTextClients(access, selfEmployed) {
    const a = access || {};
    const caps = a.caps || [];
    if (a.owner || selfEmployed || caps.indexOf('client_texts') !== -1) return true;
    return !!PENDING_CAPS.client_texts && caps.indexOf(PENDING_CAPS.client_texts) !== -1;
}

export function useCanTextClients() {
    const { access } = useAccess();
    return canTextClients(access, isSelfEmployedMe());
}
